import * as Sentry from '@sentry/nextjs';
import {
  scrubSentryEvent,
  scrubSentryTransaction,
  scrubPHIString,
  scrubPHI,
  PHI_ROUTE_PATTERNS,
  SAFE_SENTRY_CAPTURE_TAG,
  isLikelyExternalDomMutationEvent,
  isLikelyNextStaticAssetDriftEvent,
  isLoopbackSentryEvent,
  isSafeSentryCapture,
  scrubSafeSentryPhiEvent,
} from './src/lib/sentry-scrub';
import {
  installClientErrorTelemetry,
  sanitizeRoutePathForSentry,
} from './src/lib/sentry-client-observability';

const sentryDsn = process.env.NEXT_PUBLIC_SENTRY_DSN;
const sentryEnabled =
  !!sentryDsn &&
  (process.env.NODE_ENV === 'production' ||
    process.env.NEXT_PUBLIC_SENTRY_ENABLE_IN_DEVELOPMENT === 'true');

/**
 * Client-side Sentry configuration.
 *
 * PHI handling: applies recursive scrubbing via beforeSend + beforeBreadcrumb,
 * plus disables session replay entirely (would otherwise capture rendered DOM
 * including care plans, medication lists, clinical notes).
 */
Sentry.init({
  dsn: sentryDsn,
  enabled: sentryEnabled,
  environment: process.env.VERCEL_ENV || process.env.NODE_ENV,
  sendDefaultPii: false,
  tracesSampleRate: 0.1,
  replaysSessionSampleRate: 0,
  replaysOnErrorSampleRate: 0,
  // installClientErrorTelemetry provides the same browser fallbacks with
  // Statura route, identity, and transport classification. Keeping Sentry's
  // default GlobalHandlers as well records every unhandled rejection twice.
  integrations(defaultIntegrations) {
    return defaultIntegrations.filter((integration) => integration.name !== 'GlobalHandlers');
  },
  // Expected user-facing business-rule rejections that are surfaced to the user
  // as guidance (via toastFail / the signing panel) and thrown through the
  // `if (!result.success) throw new Error(result.error)` mutation pattern. These
  // are NOT crashes — they only fire on genuine data-completeness states (no
  // verified signer linked, expired signer authority, no recipient chosen), so
  // they must not be reported as production Sentry errors.
  ignoreErrors: [
    /No verified signer is available/i,
    /signer'?s authority is no longer current/i,
    /Re-verify the participant or representative signer/i,
    /Select an authorised recipient before/i,
    /assessment_next_review_required/i,
  ],
  beforeSend(event) {
    const url = event.request?.url ?? '';
    if (isLoopbackSentryEvent(event)) return null;
    if (isLikelyNextStaticAssetDriftEvent(event)) return null;
    if (isLikelyExternalDomMutationEvent(event)) return null;
    if (isSafeSentryCapture(event) && PHI_ROUTE_PATTERNS.some((re) => re.test(url))) {
      return scrubSentryEvent(scrubSafeSentryPhiEvent(event));
    }
    if (PHI_ROUTE_PATTERNS.some((re) => re.test(url))) return null;
    return scrubSentryEvent(event);
  },
  beforeSendTransaction(event) {
    const url = event.request?.url ?? event.transaction ?? '';
    if (isLoopbackSentryEvent(event)) return null;
    if (PHI_ROUTE_PATTERNS.some((re) => re.test(url))) return null;
    return scrubSentryTransaction(event);
  },
  beforeBreadcrumb(breadcrumb) {
    if (breadcrumb.message) breadcrumb.message = scrubPHIString(breadcrumb.message);
    if (breadcrumb.data) breadcrumb.data = scrubPHI(breadcrumb.data) as typeof breadcrumb.data;
    return breadcrumb;
  },
});

Sentry.addEventProcessor((event) => {
  if (typeof window === 'undefined') return event;
  const routePath = sanitizeRoutePathForSentry(window.location.pathname);
  const tags: Record<string, string> = {
    ...event.tags,
    route_path: routePath,
    route_class: routePath,
  };
  if (event.tags?.[SAFE_SENTRY_CAPTURE_TAG]) {
    tags[SAFE_SENTRY_CAPTURE_TAG] = String(event.tags[SAFE_SENTRY_CAPTURE_TAG]);
  }
  event.request = {
    ...event.request,
    url: window.location.href,
  };
  event.tags = tags;
  return event;
});

installClientErrorTelemetry();

export const onRouterTransitionStart = Sentry.captureRouterTransitionStart;
